import type { ReactElement } from 'react';
import { PlatformRole, type UserDto } from '@gr/core/accounts/identity/dto';
import { useT } from '../../../../app/di';
import { formatDate, formatDateTime } from '../../../../shared/format';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { Button } from '../../../../ui-kit/Button/Button';
import { DataTable, type DataColumn } from '../../../../ui-kit/DataTable/DataTable';
import { EmptyState } from '../../../../ui-kit/EmptyState/EmptyState';

export interface UserTableProps {
  users: readonly UserDto[];
  /** Себя нельзя заблокировать (IAM-CC). */
  currentUserId: string | null;
  pending: boolean;
  onToggleDisabled(user: UserDto): void;
  onToggleAdmin(user: UserDto): void;
  onResetPassword(user: UserDto): void;
  /** PLAN-UC-4: название тарифа пользователя; без него столбца нет. */
  planOf?(user: UserDto): string | null;
  /** PLAN-UC-4: открыть тариф пользователя. */
  onOpenPlan?(user: UserDto): void;
}

/** IAM-UC-5…7: пользователи системы с ролью и статусом. */
export function UserTable({ users, currentUserId, pending, onToggleDisabled, onToggleAdmin, onResetPassword, planOf, onOpenPlan }: UserTableProps): ReactElement {
  const { t, locale } = useT();
  const planColumn: DataColumn<UserDto>[] = planOf ? [{ key: 'plan', header: t('identity.users.plan'), cell: (user) => planOf(user) ?? <span className="u-muted">—</span> }] : [];
  return (
    <DataTable
      label={t('identity.users.title')}
      rows={users}
      rowKey={(user) => user.id}
      empty={<EmptyState title={t('identity.users.empty')} />}
      columns={[
        {
          key: 'login',
          header: t('identity.users.login'),
          primary: true,
          cell: (user) => (
            <>
              <strong>{user.login}</strong>
              <div className="u-muted u-small">{formatDate(user.createdAt, locale)}</div>
            </>
          ),
        },
        { key: 'name', header: t('identity.users.displayName'), cell: (user) => user.displayName },
        { key: 'role', header: t('identity.users.platformRole'), cell: (user) => t(`identity.users.roles.${user.platformRole}`) },
        ...planColumn,
        // IAM-UC-6.CC-3: вход, открытие или закрытие приложения; кто онлайн сейчас — раздел «Онлайн».
        { key: 'lastSeen', header: t('identity.users.lastSeen'), cell: (user) => (user.lastSeenAt ? formatDateTime(user.lastSeenAt, locale) : <span className="u-muted">{t('identity.users.neverSeen')}</span>) },
        {
          key: 'status',
          header: t('identity.users.status'),
          cell: (user) => (user.disabled ? <Badge tone="danger">{t('identity.users.disabled')}</Badge> : <Badge tone="success">{t('identity.users.active')}</Badge>),
        },
        {
          key: 'actions',
          header: t('identity.users.actions'),
          actions: true,
          cell: (user) => (
            <div className="u-row">
              {user.id !== currentUserId ? (
                <Button size="sm" variant={user.disabled ? 'secondary' : 'ghost'} disabled={pending} onClick={() => onToggleDisabled(user)}>
                  {user.disabled ? t('identity.users.enable') : t('identity.users.disable')}
                </Button>
              ) : null}
              <Button size="sm" variant="ghost" disabled={pending} onClick={() => onToggleAdmin(user)}>
                {user.platformRole === PlatformRole.Admin ? t('identity.users.makeUser') : t('identity.users.makeAdmin')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onResetPassword(user)}>
                {t('identity.users.resetPassword')}
              </Button>
              {onOpenPlan ? (
                <Button size="sm" variant="ghost" onClick={() => onOpenPlan(user)}>
                  {t('identity.users.openPlan')}
                </Button>
              ) : null}
            </div>
          ),
        },
      ]}
    />
  );
}
